// Portal Cases List Page
import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { policyStatusLabel } from '../lib/policyStatus';
import { 
  Search, 
  Plus, 
  Filter,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Calendar,
  Download,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { CaseExportDialog } from '../components/CaseExportDialog';
import { RenewCaseDialog } from '../components/RenewCaseDialog';
import { EnrollmentTypeBadge } from '../components/EnrollmentTypeBadge';
import { RenewalStatusBadge } from '../components/RenewalStatusBadge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const statusColors = {
  'New Submission': 'bg-sky-100 text-sky-700',
  'Under Review': 'bg-amber-100 text-amber-700',
  'Missing Information': 'bg-orange-100 text-orange-700',
  'Documents Due': 'bg-orange-100 text-orange-700',
  'Documents Received': 'bg-teal-100 text-teal-700',
  'Pending Client Contact': 'bg-purple-100 text-purple-700',
  'Pending Marketplace Application': 'bg-indigo-100 text-indigo-700',
  'Application Submitted': 'bg-blue-100 text-blue-700',
  'Enrolled': 'bg-green-100 text-green-700',
  'Not Eligible': 'bg-slate-100 text-slate-700',
  'Closed': 'bg-slate-100 text-slate-700',
  'Pending Renewal Review': 'bg-purple-100 text-purple-700',
};

const consentColors = {
  'Consent Received': 'bg-green-100 text-green-700',
  'Consent Pending': 'bg-amber-100 text-amber-700',
  'Consent Requested': 'bg-sky-100 text-sky-700',
  'Consent Declined': 'bg-red-100 text-red-700',
  'Consent Not Required': 'bg-slate-100 text-slate-700',
};

const PAGE_SIZE = 100;

const policyStatusColors = {
  'Active': 'bg-green-100 text-green-700',
  'Pending': 'bg-amber-100 text-amber-700',
  'Superseded': 'bg-slate-200 text-slate-600',
  'Terminated': 'bg-orange-100 text-orange-700',
  'Cancelled': 'bg-rose-100 text-rose-700',
};

const premiumSupportColors = {
  'Active': 'bg-green-100 text-green-700',
  'Pending': 'bg-amber-100 text-amber-700',
  'Not Eligible': 'bg-slate-100 text-slate-700',
  'Suspended': 'bg-orange-100 text-orange-700',
  'Closed': 'bg-slate-100 text-slate-700',
};

const formatDate = (val) => {
  if (!val) return '—';
  const s = String(val).trim();
  let d;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, day] = s.split('T')[0].split('-').map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = new Date(s);
  }
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

function SortHeader({ label, field, sortBy, sortDir, onSort }) {
  const active = sortBy === field;
  return (
    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">
      <button type="button" onClick={() => onSort(field)} className="inline-flex items-center gap-1 hover:text-slate-700" data-testid={`sort-${field}`}>
        {label}
        {active
          ? (sortDir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />)
          : <ChevronsUpDown className="h-3.5 w-3.5 text-slate-300" />}
      </button>
    </th>
  );
}

export function CasesListPage() {
  const { hasPermission, activeWorkspaceId, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(0);
  const [years, setYears] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [constants, setConstants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [renewTarget, setRenewTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // {mode:'single'|'bulk', case?, ids?}
  const [deleting, setDeleting] = useState(false);
  const didInitYear = useRef(false);
  
  // Filters from URL
  const selectedYear = searchParams.get('coverage_year_id') || '';
  const selectedStatus = searchParams.get('status') || '';
  const selectedConsent = searchParams.get('consent_status') || '';
  const selectedCarrier = searchParams.get('insurance_carrier') || '';
  const selectedMarket = searchParams.get('market_type') || '';
  const selectedProgram = searchParams.get('program_type') || '';
  const selectedPolicyStatus = searchParams.get('policy_status') || '';
  const selectedPremium = searchParams.get('premium_support_status') || '';
  const selectedOffice = searchParams.get('office') || '';
  const needsAttention = searchParams.get('needs_attention') === 'true';
  const selectedEnrollment = searchParams.get('enrollment_type') || '';
  const selectedRenewal = searchParams.get('renewal_status') || '';

  useEffect(() => {
    document.title = 'Cases · Latinos Salud';
    loadFilters();
  }, []);

  useEffect(() => {
    loadCases();
  }, [searchParams, page, activeWorkspaceId]);

  // Debounced search: search as you type
  useEffect(() => {
    const currentQ = searchParams.get('q') || '';
    if (search === currentQ) return;
    const timer = setTimeout(() => {
      updateFilter('q', search);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadFilters = async () => {
    try {
      const [yearsData, carriersData, constantsData] = await Promise.all([
        portalApi.getCoverageYears(),
        portalApi.getCarriers().catch(() => []),
        portalApi.getConstants().catch(() => null),
      ]);
      setYears(yearsData.filter(y => y.accessible !== false));
      setCarriers(carriersData);
      setConstants(constantsData);
    } catch (err) {
      console.error('Error loading filters:', err);
    }
  };

  // Default the coverage-year filter ONCE, but only after BOTH the coverage years
  // and the authenticated user are available (avoids a race where `user` is still
  // null on mount and an agent wrongly defaults to the admin year 2026).
  useEffect(() => {
    if (didInitYear.current) return;
    if (!user || years.length === 0) return;
    if (searchParams.get('coverage_year_id') !== null) { didInitYear.current = true; return; }
    didInitYear.current = true;
    let def;
    if (user.role === 'agent_producer') {
      // Agents are onboarded for the upcoming Open Enrollment — default their view to
      // the latest active coverage year (currently 2027). Admins/others keep the default.
      const activeYears = years.filter(y => y.is_active === true && y.is_archived !== true);
      def = activeYears.slice().sort((a, b) => (b.year || 0) - (a.year || 0))[0]
          || years.find(y => y.is_default) || years.find(y => y.is_active);
    } else {
      def = years.find(y => y.is_default) || years.find(y => y.is_active);
    }
    if (def) {
      const p = new URLSearchParams(searchParams);
      p.set('coverage_year_id', def.id);
      setSearchParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, years, searchParams]);

  const loadCases = async () => {
    setLoading(true);
    setSelectedIds([]);
    setSelectAllMatching(false);
    try {
      const params = {};
      ['q', 'coverage_year_id', 'status', 'consent_status', 'insurance_carrier', 'market_type', 'program_type', 'policy_status', 'premium_support_status', 'office', 'sort_by', 'sort_dir', 'needs_attention', 'enrollment_type', 'renewal_status'].forEach((key) => {
        const val = searchParams.get(key);
        if (val && val !== 'all') params[key] = val;
      });
      params.skip = page * PAGE_SIZE;
      params.limit = PAGE_SIZE;
      if (activeWorkspaceId) params.workspace_id = activeWorkspaceId;
      
      const data = await portalApi.getCases(params);
      setCases(data.items || []);
      setTotal(data.total || 0);

      // Enrollment counters reflect the same filters EXCEPT the enrollment_type filter + paging.
      const summaryParams = { ...params };
      delete summaryParams.skip; delete summaryParams.limit; delete summaryParams.enrollment_type;
      portalApi.getEnrollmentSummary(summaryParams).then(setSummary).catch(() => setSummary(null));
    } catch (err) {
      console.error('Error loading cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    // coverage_year_id keeps an explicit 'all' so it overrides the default-year behaviour
    if (value && (value !== 'all' || key === 'coverage_year_id')) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setPage(0);
    setSearchParams(newParams);
  };

  const sortBy = searchParams.get('sort_by') || 'created_at';
  const sortDir = searchParams.get('sort_dir') || 'desc';

  const toggleSort = (field) => {
    const newParams = new URLSearchParams(searchParams);
    if (sortBy === field) {
      newParams.set('sort_dir', sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      newParams.set('sort_by', field);
      newParams.set('sort_dir', 'asc');
    }
    setPage(0);
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateFilter('q', search);
  };

  // ---- Selection for export ----
  const pageIds = cases.map((c) => c.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const toggleRow = (id, on) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id));
  };
  const togglePage = () => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => allPageSelected ? prev.filter((id) => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]);
  };
  const clearSelection = () => { setSelectedIds([]); setSelectAllMatching(false); };

  const [bulkStatusValue, setBulkStatusValue] = useState('Closed');
  const [applyingStatus, setApplyingStatus] = useState(false);
  const handleBulkStatus = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Change status of ${selectedIds.length} case(s) to "${bulkStatusValue}"?`)) return;
    setApplyingStatus(true);
    try {
      const res = await portalApi.bulkCaseStatus(selectedIds, bulkStatusValue);
      toast.success(`Updated ${res.updated} case(s) to "${bulkStatusValue}".`);
      clearSelection();
      await loadCases();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setApplyingStatus(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.mode === 'single') {
        await portalApi.deleteCase(deleteTarget.case.id);
        toast.success('Case deleted');
      } else {
        const res = await portalApi.bulkDeleteCases(deleteTarget.ids);
        toast.success(`Deleted ${res.deleted} case${res.deleted === 1 ? '' : 's'}`);
      }
      setDeleteTarget(null);
      clearSelection();
      await loadCases();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const exportFilters = {
    q: search || undefined, status: selectedStatus || undefined, office: selectedOffice || undefined,
    insurance_carrier: selectedCarrier || undefined, policy_status: selectedPolicyStatus || undefined,
    coverage_year_id: (selectedYear && selectedYear !== 'all') ? selectedYear : undefined, consent_status: selectedConsent || undefined,
    program_type: selectedProgram || undefined, market_type: selectedMarket || undefined,
    premium_support_status: selectedPremium || undefined,
    enrollment_type: selectedEnrollment || undefined,
  };
  const useManual = selectedIds.length > 0 && !selectAllMatching;
  const exportCount = useManual ? selectedIds.length : total;
  const exportSource = useManual ? 'Manually selected cases' : 'Current filtered results';

  return (
    <div className="space-y-6" data-testid="cases-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cases</h1>
          <p className="text-slate-500 mt-1">{total} cases found</p>
        </div>
        <div className="flex items-center gap-3">
          {hasPermission('cases.export') && (
            <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="open-export-btn">
              <Download className="h-4 w-4 mr-2" />
              Export{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
            </Button>
          )}
          {hasPermission('cases.create') && (
            <Button asChild data-testid="new-case-btn">
              <Link to={`/portal/cases/new${selectedYear ? `?year=${selectedYear}` : ''}`}>
                <Plus className="h-4 w-4 mr-2" />
                New Case
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Enrollment summary counters (update with the selected year + filters). Enrollment Type is
          tracked only from 2027; on historical years the New/Renewal/Unverified split is replaced
          by a single "No rastreado" card so the zeros are not misleading. */}
      {(() => {
        const isHistorical = summary && summary.not_tracked > 0
          && (summary.new + summary.renewal + summary.unverified) === 0;
        return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="enrollment-summary">
        <button type="button" onClick={() => updateFilter('enrollment_type', 'all')}
          className={`text-left rounded-xl p-4 border transition-colors ${!selectedEnrollment ? 'border-slate-400 bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
          data-testid="counter-total">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Cases</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{summary ? summary.total : total}</p>
        </button>
        {isHistorical ? (
          <div className="text-left rounded-xl p-4 border border-slate-200 bg-white sm:col-span-3"
            data-testid="counter-not-tracked">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Enrollment Type</p>
            <p className="text-base font-semibold text-slate-600 mt-1">Not Tracked · Enrollment type is tracked from 2027</p>
          </div>
        ) : (
          <>
            <button type="button" onClick={() => updateFilter('enrollment_type', 'New')}
              className={`text-left rounded-xl p-4 border transition-colors ${selectedEnrollment === 'New' ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              data-testid="counter-new">
              <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">New Enrollments</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{summary ? summary.new : '—'}</p>
            </button>
            <button type="button" onClick={() => updateFilter('enrollment_type', 'Renewal')}
              className={`text-left rounded-xl p-4 border transition-colors ${selectedEnrollment === 'Renewal' ? 'border-green-400 bg-green-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              data-testid="counter-renewal">
              <p className="text-xs font-medium text-green-600 uppercase tracking-wider">Renewals</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{summary ? summary.renewal : '—'}</p>
            </button>
            <button type="button" onClick={() => updateFilter('enrollment_type', 'Unverified')}
              className={`text-left rounded-xl p-4 border transition-colors ${selectedEnrollment === 'Unverified' ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              data-testid="counter-unverified">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">Unverified</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{summary ? summary.unverified : '—'}</p>
            </button>
          </>
        )}
      </div>
        );
      })()}

      {/* Renewal workflow tracking for the Open Enrollment cycle — computed from the eligible
          source-year (previous year) population, not from the number of destination cases. */}
      {summary?.renewal_workflow && summary.renewal_workflow.eligible > 0 && (() => {
        // Forward the filters the summary counts respect so the tracking list matches the card.
        const extra = (selectedOffice ? `&office=${encodeURIComponent(selectedOffice)}` : '')
          + (searchParams.get('case_manager_id') ? `&case_manager_id=${searchParams.get('case_manager_id')}` : '');
        const trackUrl = (st) => `/portal/renewals?target_year_id=${selectedYear}${st ? `&status=${st}` : ''}${extra}`;
        return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="renewal-workflow-summary">
          <button type="button" onClick={() => navigate(trackUrl(''))}
            className="text-left rounded-xl p-4 border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer"
            data-testid="counter-renewal-eligible">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Eligible for Renewal</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{summary.renewal_workflow.eligible}</p>
          </button>
          <button type="button" onClick={() => navigate(trackUrl('pending'))}
            className="text-left rounded-xl p-4 border border-orange-200 bg-orange-50 hover:bg-orange-100 transition-colors cursor-pointer"
            data-testid="counter-renewal-pending">
            <p className="text-xs font-medium text-orange-600 uppercase tracking-wider">Pending Renewal</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{summary.renewal_workflow.pending}</p>
          </button>
          <button type="button" onClick={() => navigate(trackUrl('in_progress'))}
            className="text-left rounded-xl p-4 border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer"
            data-testid="counter-renewal-in-progress">
            <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">In Progress</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{summary.renewal_workflow.in_progress}</p>
          </button>
          <button type="button" onClick={() => navigate(trackUrl('renewed'))}
            className="text-left rounded-xl p-4 border border-green-200 bg-green-50 hover:bg-green-100 transition-colors cursor-pointer"
            data-testid="counter-renewal-renewed">
            <p className="text-xs font-medium text-green-600 uppercase tracking-wider">Renewed</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{summary.renewal_workflow.renewed}</p>
          </button>
        </div>
        );
      })()}

      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex flex-col gap-4">
          <form onSubmit={handleSearch} className="w-full">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Search by name, Athena ID, case manager..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-full"
                data-testid="search-input"
              />
            </div>
          </form>
          
          <div className="flex flex-wrap gap-3">
            <Select value={selectedYear || 'all'} onValueChange={(v) => updateFilter('coverage_year_id', v)}>
              <SelectTrigger className="w-[180px]">
                <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {years.map((year) => (
                  <SelectItem key={year.id} value={year.id}>{year.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedEnrollment || 'all'} onValueChange={(v) => updateFilter('enrollment_type', v)}>
              <SelectTrigger className="w-[190px]" data-testid="enrollment-type-filter">
                <SelectValue placeholder="All Enrollment Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Enrollment Types</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Renewal">Renewal</SelectItem>
                <SelectItem value="Unverified">Unverified</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedRenewal || 'all'} onValueChange={(v) => updateFilter('renewal_status', v)}>
              <SelectTrigger className="w-[190px]" data-testid="renewal-status-filter">
                <SelectValue placeholder="All Renewal Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Renewal Status</SelectItem>
                <SelectItem value="pending">Pending Renewal</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="renewed">Renewed</SelectItem>
                <SelectItem value="not_applicable">Not Applicable</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedStatus || 'all'} onValueChange={(v) => updateFilter('status', v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.keys(statusColors).map((status) => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedConsent || 'all'} onValueChange={(v) => updateFilter('consent_status', v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Consent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Consent</SelectItem>
                {Object.keys(consentColors).map((status) => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedOffice || 'all'} onValueChange={(v) => updateFilter('office', v)}>
              <SelectTrigger className="w-[170px]" data-testid="filter-office">
                <SelectValue placeholder="All Offices" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Offices</SelectItem>
                {(constants?.offices || []).map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedProgram || 'all'} onValueChange={(v) => updateFilter('program_type', v)}>
              <SelectTrigger className="w-[160px]" data-testid="filter-program">
                <SelectValue placeholder="All Programs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs</SelectItem>
                {(constants?.program_types || ['PrEP', 'HIV Care']).map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedMarket || 'all'} onValueChange={(v) => updateFilter('market_type', v)}>
              <SelectTrigger className="w-[170px]" data-testid="filter-market">
                <SelectValue placeholder="All Markets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Markets</SelectItem>
                {(constants?.market_types || ['Marketplace', 'Off-Exchange']).map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedCarrier || 'all'} onValueChange={(v) => updateFilter('insurance_carrier', v)}>
              <SelectTrigger className="w-[180px]" data-testid="filter-carrier">
                <SelectValue placeholder="All Carriers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Carriers</SelectItem>
                {carriers.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedPolicyStatus || 'all'} onValueChange={(v) => updateFilter('policy_status', v)}>
              <SelectTrigger className="w-[170px]" data-testid="filter-policy-status">
                <SelectValue placeholder="All Policy Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Policy Status</SelectItem>
                {(constants?.policy_statuses || ['Active', 'Pending', 'Terminated', 'Cancelled']).map((s) => (
                  <SelectItem key={s} value={s}>{policyStatusLabel(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedPremium || 'all'} onValueChange={(v) => updateFilter('premium_support_status', v)}>
              <SelectTrigger className="w-[190px]" data-testid="filter-premium-support">
                <SelectValue placeholder="All Premium Support" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Premium Support</SelectItem>
                {(constants?.premium_support_statuses || ['Active', 'Pending', 'Not Eligible', 'Suspended', 'Closed']).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Cases List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
        </div>
      ) : cases.length === 0 ? (
        <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No cases found</h3>
          <p className="text-slate-500 mb-6">Try adjusting your filters or create a new case.</p>
          {hasPermission('cases.create') && (
            <Button asChild>
              <Link to="/portal/cases/new">
                <Plus className="h-4 w-4 mr-2" />
                Create Case
              </Link>
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {(selectedIds.length > 0 || selectAllMatching) && (
            <div className="flex items-center justify-between gap-3 px-6 py-2.5 bg-sky-50 border-b border-sky-100 text-sm" data-testid="selection-banner">
              <span className="text-sky-800">
                {selectAllMatching
                  ? `All ${total} matching cases selected.`
                  : `${selectedIds.length} selected.`}
                {!selectAllMatching && allPageSelected && total > pageIds.length && (
                  <button className="ml-2 font-semibold underline" onClick={() => setSelectAllMatching(true)} data-testid="select-all-matching-btn">
                    Select all {total} matching cases
                  </button>
                )}
              </span>
              <button className="text-slate-500 hover:text-slate-700" onClick={clearSelection} data-testid="clear-selection-btn">Clear selection</button>
            </div>
          )}
          {selectedIds.length > 0 && !selectAllMatching && (
            <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-2 bg-slate-50 border-b border-slate-200">
              {isAdmin && (
                <div className="flex items-center gap-2 mr-auto" data-testid="bulk-status-controls">
                  <span className="text-sm text-slate-600">Set status:</span>
                  <Select value={bulkStatusValue} onValueChange={setBulkStatusValue}>
                    <SelectTrigger className="w-[200px] h-9" data-testid="bulk-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(statusColors).map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" disabled={applyingStatus} onClick={handleBulkStatus} data-testid="apply-bulk-status-btn">
                    {applyingStatus ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Apply to {selectedIds.length}
                  </Button>
                </div>
              )}
              {isAdmin && (
                <Button variant="destructive" size="sm" onClick={() => setDeleteTarget({ mode: 'bulk', ids: selectedIds })} data-testid="bulk-delete-btn">
                  <Trash2 className="h-4 w-4 mr-2" />Delete {selectedIds.length} selected
                </Button>
              )}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <Checkbox checked={allPageSelected} onCheckedChange={togglePage} data-testid="select-page-checkbox" aria-label="Select page" />
                  </th>
                  <SortHeader label="Athena ID" field="athena_id" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Patient Name" field="patient_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <SortHeader label="Year" field="coverage_year" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Enrollment Type</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Renewal Status</th>
                  <SortHeader label="Status" field="policy_status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Premium Support</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Consent</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Case Manager</th>
                  <SortHeader label="Office" field="office" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {cases.map((caseItem) => (
                  <tr key={caseItem.id} className="hover:bg-slate-50" data-testid={`case-row-${caseItem.athena_id}`}>
                    <td className="px-4 py-4 w-10">
                      <Checkbox checked={selectedIds.includes(caseItem.id)} onCheckedChange={(v) => toggleRow(caseItem.id, !!v)} data-testid={`select-case-${caseItem.athena_id}`} aria-label="Select case" />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {!caseItem.opened && (
                          <span className="h-2.5 w-2.5 rounded-full bg-sky-500 ring-2 ring-sky-100 flex-shrink-0" title="New — not opened yet" data-testid={`new-indicator-${caseItem.athena_id}`} />
                        )}
                        {caseItem.urgent && <AlertTriangle className="h-4 w-4 text-red-500" />}
                        {caseItem.needs_attention && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                        <Link to={`/portal/cases/${caseItem.id}`} className="font-medium text-sky-600 hover:text-sky-700 hover:underline" data-testid={`athena-link-${caseItem.athena_id}`}>
                          {caseItem.athena_id || 'Open case'}
                        </Link>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700" data-testid={`patient-name-${caseItem.id}`}>
                      {caseItem.patient_name || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                      {caseItem.coverage_year || (caseItem.coverage_year_label || '').match(/\d{4}/)?.[0] || caseItem.coverage_year_label}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`enrollment-type-${caseItem.id}`}>
                      <EnrollmentTypeBadge type={caseItem.enrollment_type} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`renewal-status-cell-${caseItem.id}`}>
                      <RenewalStatusBadge status={caseItem.renewal_display_status} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`policy-status-${caseItem.athena_id}`}>
                      {caseItem.policy?.policy_status ? (
                        <Badge className={policyStatusColors[caseItem.policy.policy_status] || 'bg-slate-100 text-slate-700'}>
                          {caseItem.policy.policy_status}
                        </Badge>
                      ) : (
                        <span className="text-sm text-slate-400">No policy</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`premium-support-${caseItem.athena_id}`}>
                      <Badge className={premiumSupportColors[caseItem.premium_support_status] || 'bg-slate-100 text-slate-700'}>
                        {caseItem.premium_support_status || 'Pending'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge className={consentColors[caseItem.consent_status] || 'bg-slate-100 text-slate-700'}>
                        {caseItem.consent_status?.replace('Consent ', '')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                      {caseItem.case_manager_name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                      {caseItem.office}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/portal/cases/${caseItem.id}`}>
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </Button>
                        {caseItem.renewal_status === 'renewed' ? (
                          <span
                            title="Renewed"
                            aria-label="Renewed"
                            data-testid={`renew-status-${caseItem.id}`}
                            className="inline-flex items-center justify-center h-8 w-8 text-green-600"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </span>
                        ) : (caseItem.renewal_status === 'pending' || caseItem.renewal_status === 'in_progress') ? (
                          hasPermission('agent.producer') ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className={caseItem.renewal_status === 'in_progress'
                                ? 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'
                                : 'text-orange-500 hover:text-orange-700 hover:bg-orange-50'}
                              onClick={() => setRenewTarget(caseItem)}
                              data-testid={`renew-case-${caseItem.id}`}
                              aria-label={caseItem.renewal_status === 'in_progress' ? 'Complete Renewal' : 'Renew case'}
                              title={caseItem.renewal_status === 'in_progress'
                                ? 'Complete renewal in progress'
                                : `Renew for ${caseItem.renewal_target_year || 'next year'}`}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          ) : (
                            <span
                              title={caseItem.renewal_status === 'in_progress' ? 'In Progress' : 'Pending Renewal'}
                              aria-label={caseItem.renewal_status === 'in_progress' ? 'In Progress' : 'Pending Renewal'}
                              data-testid={`renew-status-${caseItem.id}`}
                              className={`inline-flex items-center justify-center h-8 w-8 ${caseItem.renewal_status === 'in_progress' ? 'text-amber-500' : 'text-orange-500'}`}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </span>
                          )
                        ) : (
                          <span className="inline-flex h-8 w-8" aria-hidden="true" />
                        )}
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                            onClick={() => setDeleteTarget({ mode: 'single', case: caseItem })}
                            data-testid={`delete-case-${caseItem.athena_id}`}
                            aria-label="Delete case"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200" data-testid="pagination-bar">
            <p className="text-sm text-slate-500">
              Showing {total === 0 ? 0 : page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              <span className="text-sm text-slate-600">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
                data-testid="next-page-btn"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <CaseExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        workspaceId={activeWorkspaceId}
        filters={exportFilters}
        exportCount={exportCount}
        source={exportSource}
        selectedIds={useManual ? selectedIds : null}
      />

      {renewTarget && (
        <RenewCaseDialog
          caseItem={renewTarget}
          open={!!renewTarget}
          onOpenChange={(o) => { if (!o) setRenewTarget(null); }}
          onRenewed={() => { setRenewTarget(null); loadCases(); }}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-case-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.mode === 'bulk'
                ? `Delete ${deleteTarget?.ids?.length} cases?`
                : `Delete case ${deleteTarget?.case?.athena_id || ''}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes {deleteTarget?.mode === 'bulk' ? 'these cases' : 'this case'} and
              their policies, documents and notes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CasesListPage;
